import { CheckCircle2, Lock, Circle } from "lucide-react";
import { cn } from "@/lib/utils";

interface Section {
  section_key: string;
  section_name: string;
  section_order: number;
}

interface SectionStepProgress {
  completed: number;
  total: number;
}

interface JourneySectionProgressProps {
  sections: Section[];
  currentSection: string;
  completedSections: string[];
  sectionProgress: Record<string, SectionStepProgress>;
}

export function JourneySectionProgress({
  sections,
  currentSection,
  completedSections,
  sectionProgress,
}: JourneySectionProgressProps) {
  const sortedSections = [...sections].sort((a, b) => a.section_order - b.section_order);

  return (
    <div className="w-full">
      {/* Desktop View */}
      <div className="hidden sm:flex items-center justify-between relative">
        {/* Progress Line */}
        <div className="absolute top-4 left-0 right-0 h-0.5 bg-muted -z-10">
          <div
            className="h-full bg-gold transition-all duration-500"
            style={{
              width: `${(completedSections.length / sortedSections.length) * 100}%`,
            }}
          />
        </div>

        {sortedSections.map((section, index) => {
          const isCompleted = completedSections.includes(section.section_key);
          const isCurrent = section.section_key === currentSection;
          const isLocked = !isCompleted && !isCurrent;

          return (
            <div key={section.section_key} className="flex flex-col items-center">
              {/* Circle Indicator */}
              <div
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300",
                  isCompleted && "bg-green-100 text-green-600 border-2 border-green-500",
                  isCurrent && "bg-gold text-charcoal border-2 border-gold shadow-lg scale-110",
                  isLocked && "bg-muted text-muted-foreground border-2 border-muted-foreground/30"
                )}
              >
                {isCompleted ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : isLocked ? (
                  <Lock className="w-4 h-4" />
                ) : (
                  <Circle className="w-4 h-4 fill-current" />
                )}
              </div>

              {/* Section Name */}
              <span
                className={cn(
                  "text-xs mt-2 text-center max-w-[100px] transition-colors",
                  isCurrent && "text-charcoal font-bold",
                  isCompleted && "text-green-600",
                  isLocked && "text-charcoal/50"
                )}
              >
                {section.section_name}
              </span>
              
              {/* Step Progress */}
              {sectionProgress[section.section_key] && (
                <span
                  className={cn(
                    "text-[10px] text-center transition-colors",
                    isCompleted && "text-green-500",
                    isCurrent && "text-charcoal/70",
                    isLocked && "text-charcoal/40"
                  )}
                >
                  {isCompleted 
                    ? "Complete" 
                    : `Step ${sectionProgress[section.section_key].completed + 1} of ${sectionProgress[section.section_key].total}`
                  }
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile View - Compact */}
      <div className="sm:hidden">
        <div className="flex items-center justify-center gap-2 mb-2">
          {sortedSections.map((section) => {
            const isCompleted = completedSections.includes(section.section_key);
            const isCurrent = section.section_key === currentSection;
            const isLocked = !isCompleted && !isCurrent;

            return (
              <div
                key={section.section_key}
                className={cn(
                  "w-3 h-3 rounded-full transition-all duration-300",
                  isCompleted && "bg-green-500",
                  isCurrent && "bg-gold scale-125",
                  isLocked && "bg-muted-foreground/30"
                )}
              />
            );
          })}
        </div>
        
        {/* Current Section Name & Progress */}
        <p className="text-center text-sm font-bold text-charcoal">
          {sortedSections.find(s => s.section_key === currentSection)?.section_name}
        </p>
        {sectionProgress[currentSection] && (
          <p className="text-center text-xs text-charcoal/70">
            Step {sectionProgress[currentSection].completed + 1} of {sectionProgress[currentSection].total}
          </p>
        )}
      </div>
    </div>
  );
}
